import { Stack, Typography, useTheme, Box } from "@mui/material";
import { t } from "i18next";
import { ChevronDown } from "lucide-react";
import {   useLocation, useNavigate, useParams } from "react-router-dom";
import { GridMain, Spinner } from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CarouselCardRestyle } from "../../components/CarouselCardRestyle/CarouselCardRestyle.component";
import { StyledButtonLink, StyledIcon } from "../../components/Video/Video.styles";
import { STATUS_LO } from "../../consts";
import { useInfiniteRelatedLearningObjectQuery } from "../../queries";
import { CardsBox } from "../../routes/Category/Category.styles";
import { useAuthStore } from "../../stores";
import type { LearningObject, MetaData } from "../../types";
import { createEndDate } from "../../utils/general";

export function ActivitiesRelatedPage(){
  const { id: idString } = useParams<{
    id: string
  }>();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);

  const {
    data: relatedLearningActivities,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading } = useInfiniteRelatedLearningObjectQuery<Record<
    string,
    LearningObject[]
  > & {

    metadata: MetaData
  }>({
    learningObjectId: idString?? "",
    pageSize:9,
    // eslint-disable-next-line max-len
    path: "/learning-catalogue/{corporateId}/{initiativeId}/learningObjects/{learningObjectId}/relatedLearningActivities?pageNumber={pageNumber}&pageSize={pageSize}"
  });

  const { state: routerState } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  //const typeLearning = useType(routerState.typeLearning, routerState.typologyLearning);
  // const typeLearning = routerState.typeLearning;
  // const typologyLearning = routerState.typologyLearning;

  const responseKey = relatedLearningActivities
    ? (Object.keys(relatedLearningActivities?.pages?.[0]).find((key) => key !== "metadata") ?? "")
    : "";

  const relatedCards = (relatedLearningActivities && responseKey)
    ? (
      relatedLearningActivities?.pages?.reduce((acc, page) => {
        const contents = page[responseKey] ?? [];
        return acc.concat(contents);
      }, [] as LearningObject[])
    ) : [];

  return (<>
    <GridMain>
      <Stack
        maxWidth="calc(100vw - (100vw - 100%))"
        minWidth={ "100%" }
      >
        <Box>
          <StyledButtonLink
            // eslint-disable-next-line no-console
            onClick={ ()=> {navigate("/esplora")} }
            sx={ {
              left: 0,
              margin: "1rem 0",
              opacity: 1,
              padding:0,
              pointerEvents: "unset",
              position: "relative",
              zIndex: 999
            } }
          >
            <StyledIcon
              icon={ "arrow_left" }
              height={ "19px" }
              width={ "24px" }
            />
            { t("explore_back") }
          </StyledButtonLink>
        </Box>
        <Typography
          variant={ "h4" }
          fontWeight={ "700" }
          padding={ "32px 0" }
          sx={ {
            [theme.breakpoints.down("sm")]: {
              fontSize: "1.5rem",
              marginBottom: "16px",
              paddingLeft: "0",
              paddingRight: "0"
            }
          } }
        >
          { routerState?.title ?? "" }
        </Typography>
        <CardsBox
          $isLoading={ isLoading }
          sx={ {
            [theme.breakpoints.down("sm")]:{
              gridTemplateColumns: "auto"
            }
          } }>
          {
            isLoading ? (
              <Spinner />
            ) : (
            //  relatedLearningActivities?.pages[0].relatedLearningActivities?.map((card) => (
              (relatedCards.map((card) => (
                <CarouselCardRestyle
                  key={  `carousel-category-detail--card--${card.id}`  }
                  booked={ card.status !== STATUS_LO.N && card.status !== STATUS_LO.T }
                  categories={ card.topicTags || [] }
                  coverPublicURL={ card.coverPublicURL }
                  disabled={ false }
                  duration={ card.duration }
                  ecmRegistration={ card.ecmRegistration }
                  ecm_specialization={ card.ecm_specialization }
                  editionNumber={ card?.editionNumber }
                  endDate={ createEndDate(card) }
                  enrollType={ card.enrollType }
                  expirationDate={ card.expirationDate }
                  id={ card.id }
                  isAutoSubscribeEnable={ card.isAutoSubscribeEnable }
                  isFavourite={ card.isFavourite }
                  isMandatory={ card.isMandatory }
                  isToj={ false }
                  learningObjectType={ card?.learningObjectType }
                  learningObjectTypology={ card.learningObjectTypology  }
                  percentageOfCompletion={ card.percentageOfCompletion }
                  shortDescription={ card.shortDescription }
                  small={ true }
                  startDate={ createEndDate(card)  }
                  status={ card.status  || "N" }
                  title={ card.title }
                  canAccessNow={ card.canAccessNow }
                  isLinkRelative={ card.isLinkRelative }
                  link={ card.link }
                  ecmType={ card.ecmType }
                  isEditorial={ card.isEditorial }
                  isSurvey={ card.isSurvey }
                  isTest={ card.isTest }
                  attributes={ card.attributes }
                  externalFileUrl={ card?.externalFileUrl }
                  isCarouselView={ false }
                />
              )))
            )
          }

        </CardsBox>
        {
          (hasNextPage && !isFetchingNextPage) ? (
            <ButtonDigited
              typology="primary"
              onClick={ () => fetchNextPage() }
              sx={ {
                color: theme.customColors.backgroundPrimaryCta,
                margin:"0 auto",
                marginBottom:"1.5rem"
              } }
            >
              <>
                { t("load_more") }
                <ChevronDown
                  size="1rem"
                />
              </>
            </ButtonDigited>
          ) : null
        }
        {
          isFetchingNextPage ? (
            <Spinner />
          ) : null
        }
      </Stack>
    </GridMain>
  </>);
}

